import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight'
import VisibilityIcon from '@mui/icons-material/Visibility'
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    IconButton,
    Menu,
    Stack,
    styled,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useLoading } from '../../context/loading_context'
import { GLOBAL_REQUEST_CONFIGS } from '../../services/generic'
import { useTranslation } from 'react-i18next'
import { useConfigs } from '../../context/configs_context'
import { useThemeMode } from '../../context/theme_mode_context'
import { LayoutConfigsInterface } from '../LayoutConfigs'

const renderColumn = (rawValue: any, column: any) => {
    let value = rawValue
    for (const property of column.property) {
        if (value && value.hasOwnProperty(property)) value = value[property]
        else {
            value = ''
            break
        }
    }

    value = column.parser ? column.parser(value) : value

    return column.customRender ? (
        column.customRender(rawValue, value)
    ) : (
        <Tooltip title={value} arrow>
            <Typography
                fontSize={'0.8125rem'}
                color="text.primary"
                sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {value}
            </Typography>
        </Tooltip>
    )
}

export const CustomTableCell = styled(TableCell)(({ theme }) => ({
    color: 'text.primary',
    borderBottom: '1px solid var(--border-color)',
    padding: '2px 8px',
    fontSize: '0.8125rem',
}))

export const CustomTableHeader = styled(CustomTableCell)(({ theme }) => ({
    padding: '8px 10px',
    fontSize: '0.875rem',
    fontWeight: 600,
    textAlign: 'center',
    color: 'white',
}))

export const GenericTable2 = ({
    title,
    filters,
    checkboxFilters,
    columns,
    values,
    identifier,
    selectedValues,
    subValueField,
    subValueIndentifier,
    isRecursive,
    pagination,
    actions,
    withoutPaper,
    withoutSelection,
    hideFilters,
    hideSearch,
    hidePagination,
    search,
    handleChangeSearch,
    handleExecuteAction,
    handleChangeSelectedFilter,
    handleChangeSelectedValues,
    handleClickRow,
    handleClickDetails,
    handleReload,
    handleOpenSelectedItems,
    sx,
}: any) => {
    const { t } = useTranslation()
    const { configs } = useConfigs()
    const { mode } = useThemeMode()
    const { isLoading, isLoadingFullScreen } = useLoading()
    const [columnsToHide, setColumnsToHide] = useState<string[]>(
        columns.filter((c: any) => c.defaultHidden).map((c: any) => c.name)
    )
    const [columnMenuAnchor, setColumnMenuAnchor] = useState<{
        top: number
        left: number
    }>()
    const [headerBgColor, setHeaderBgColor] = useState(
        mode === 'dark' ? '#4F46E5' : '#3a49e7'
    )
    const [headerTextColor, setHeaderTextColor] = useState('white')

    useEffect(() => {
        let nextBg = mode === 'dark' ? '#4F46E5' : '#3a49e7'
        let nextText = 'white'
        if (configs.layoutConfigs) {
            const layoutConfigs = JSON.parse(
                configs.layoutConfigs
            ) as LayoutConfigsInterface
            if (layoutConfigs.table?.headerBgColor)
                nextBg = layoutConfigs.table.headerBgColor
            if (layoutConfigs.table?.headerTextColor)
                nextText = layoutConfigs.table.headerTextColor
        }
        setHeaderBgColor(nextBg)
        setHeaderTextColor(nextText)
    }, [configs, mode])

    useEffect(() => {
        const columnDefinitions = localStorage.getItem(
            `column-definition:${title}`
        )
        if (columnDefinitions) {
            const columnDefinitionsParsed = JSON.parse(columnDefinitions)
            setColumnsToHide([...columnDefinitionsParsed.columnsToHide])
        }
    }, [])

    const columnsToShow = columns?.filter(
        (c: any) => !columnsToHide.includes(c.name)
    ).length
        ? columns.filter((c: any) => !columnsToHide.includes(c.name))
        : [
              {
                  name: '',
                  property: [],
                  parser: () => '',
              },
          ]

    const columnsToHideSet = new Set(columnsToHide)
    const visibleColumns = columns.filter(
        (c: any) => !columnsToHideSet.has(c.name)
    )
    const columnsCount = visibleColumns.length
    const maxCellWidthVw = columnsCount
        ? `${(100 / columnsCount) * 1.5}vw`
        : '200px'
    const allChecked = values.length
        ? values.every((value: any) =>
              selectedValues.includes(value[identifier])
          )
        : false
    const selectedValuesSet = new Set(selectedValues)

    return (
        <>
            <Menu
                open={!!columnMenuAnchor}
                anchorReference="anchorPosition"
                anchorPosition={columnMenuAnchor}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                onClose={() => setColumnMenuAnchor(undefined)}
            >
                <Stack p={'14px'} width={'440px'}>
                    <Typography
                        fontWeight={600}
                        textAlign={'center'}
                        fontSize={'14px'}
                        sx={{
                            borderBottom: '1px solid var(--border-color)',
                            pb: '10px',
                        }}
                    >
                        {t('Colunas a exibir')}
                    </Typography>
                    <Stack
                        direction={'row'}
                        flexWrap={'wrap'}
                        sx={{ mt: '6px' }}
                    >
                        {columns.map((column: any) => (
                            <FormControlLabel
                                label={t(column.name)}
                                control={
                                    <Checkbox
                                        checked={
                                            !columnsToHide.includes(column.name)
                                        }
                                        onChange={(e) => {
                                            const newColumnsToHide = [
                                                ...columnsToHide,
                                            ]
                                            if (!e.target.checked) {
                                                newColumnsToHide.push(
                                                    column.name
                                                )
                                            } else {
                                                const index =
                                                    newColumnsToHide.findIndex(
                                                        (c) => c === column.name
                                                    )
                                                if (index > -1)
                                                    newColumnsToHide.splice(
                                                        index,
                                                        1
                                                    )
                                            }
                                            setColumnsToHide(newColumnsToHide)

                                            const columnDefinitionsFromLs =
                                                localStorage.getItem(
                                                    `column-definition:${title}`
                                                )
                                            let columnDefinitionsToSet: any = {}

                                            if (columnDefinitionsFromLs) {
                                                columnDefinitionsToSet =
                                                    JSON.parse(
                                                        columnDefinitionsFromLs
                                                    )
                                            }

                                            columnDefinitionsToSet.columnsToHide =
                                                newColumnsToHide
                                            localStorage.setItem(
                                                `column-definition:${title}`,
                                                JSON.stringify(
                                                    columnDefinitionsToSet
                                                )
                                            )
                                        }}
                                    />
                                }
                            />
                        ))}
                    </Stack>
                </Stack>
            </Menu>
            <TableContainer
                sx={{
                    width: '100%',
                    my: '12px',
                    borderRadius: '12px',
                    border: '1px solid var(--border-color)',
                    overflow: 'hidden',
                    boxShadow:
                        '0 1px 3px rgba(16, 24, 40, 0.06), 0 4px 12px rgba(16, 24, 40, 0.04)',
                    backgroundColor: 'var(--surface)',
                    position: 'relative',
                    ...sx,
                }}
            >
                {isLoading && !isLoadingFullScreen && (
                    <Box
                        sx={{
                            position: 'absolute',
                            outline: 'none',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: '1000',
                            backgroundColor:
                                mode === 'dark'
                                    ? 'rgba(15, 23, 42, 0.55)'
                                    : 'rgba(255, 255, 255, 0.45)',
                            backdropFilter: 'blur(1px)',
                        }}
                    >
                        <CircularProgress size="44px" />
                    </Box>
                )}
                <Table stickyHeader>
                    <TableHead>
                        {columnsToShow.map(
                            (column: any, index: number, arr: any) => {
                                return (
                                    <CustomTableHeader
                                        sx={{
                                            backgroundColor: headerBgColor,
                                            color: headerTextColor,
                                            borderBottom: 'none',
                                        }}
                                    >
                                        <Stack
                                            direction={'row'}
                                            alignItems={'center'}
                                            spacing={1}
                                        >
                                            {index === 0 && (
                                                <Checkbox
                                                    sx={{
                                                        color: headerTextColor,
                                                        '&.Mui-checked': {
                                                            color: headerTextColor,
                                                        },
                                                        padding: '3px',
                                                        visibility:
                                                            !withoutSelection
                                                                ? 'visible'
                                                                : 'hidden',
                                                    }}
                                                    checked={allChecked}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        const newSelectedValues =
                                                            [...selectedValues]

                                                        for (const value of values) {
                                                            const index =
                                                                newSelectedValues.findIndex(
                                                                    (sv) =>
                                                                        sv ===
                                                                        value[
                                                                            identifier
                                                                        ]
                                                                )

                                                            if (!allChecked) {
                                                                if (
                                                                    index === -1
                                                                )
                                                                    newSelectedValues.push(
                                                                        value[
                                                                            identifier
                                                                        ]
                                                                    )
                                                            } else {
                                                                if (index > -1)
                                                                    newSelectedValues.splice(
                                                                        index,
                                                                        1
                                                                    )
                                                            }
                                                        }

                                                        handleChangeSelectedValues(
                                                            newSelectedValues
                                                        )
                                                    }}
                                                />
                                            )}
                                            <Typography
                                                textAlign={'left'}
                                                flex={1}
                                                fontSize={'0.8125rem'}
                                                fontWeight={600}
                                            >
                                                {t(column.name)}
                                            </Typography>
                                            {index === arr.length - 1 && (
                                                <IconButton
                                                    onClick={(e) => {
                                                        const rect =
                                                            e.currentTarget.getBoundingClientRect()
                                                        setColumnMenuAnchor({
                                                            top:
                                                                rect.top +
                                                                window.scrollY,
                                                            left:
                                                                rect.left +
                                                                window.scrollX,
                                                        })
                                                    }}
                                                    sx={{
                                                        color: headerTextColor,
                                                        p: '4px',
                                                    }}
                                                    size="small"
                                                >
                                                    <MoreVertIcon
                                                        sx={{
                                                            color: headerTextColor,
                                                            fontSize: '1.1rem',
                                                        }}
                                                    />
                                                </IconButton>
                                            )}
                                        </Stack>
                                    </CustomTableHeader>
                                )
                            }
                        )}
                    </TableHead>
                    <TableBody>
                        {values.map(
                            (value: any, rowIndex: number, rowArr: any) => {
                                const foundSelectedValue =
                                    selectedValuesSet.has(value[identifier])
                                        ? selectedValues.indexOf(
                                              value[identifier]
                                          )
                                        : -1

                                return (
                                    <TableRow
                                        sx={{
                                            '&:last-child .MuiTableCell-root': {
                                                borderBottom: 'none',
                                            },
                                        }}
                                    >
                                        {visibleColumns.map(
                                            (
                                                column: any,
                                                index: number,
                                                arr: any
                                            ) => (
                                                <CustomTableCell
                                                    sx={{
                                                        maxWidth:
                                                            maxCellWidthVw,
                                                    }}
                                                >
                                                    <Stack
                                                        direction={'row'}
                                                        alignItems={'center'}
                                                        spacing={0.5}
                                                    >
                                                        {index === 0 && (
                                                            <Checkbox
                                                                sx={{
                                                                    visibility:
                                                                        !withoutSelection
                                                                            ? 'visible'
                                                                            : 'hidden',
                                                                    p: '2px',
                                                                }}
                                                                checked={
                                                                    foundSelectedValue >
                                                                    -1
                                                                }
                                                                onClick={(
                                                                    e
                                                                ) => {
                                                                    e.stopPropagation()
                                                                    const newSelectedValues =
                                                                        [
                                                                            ...selectedValues,
                                                                        ]
                                                                    if (
                                                                        foundSelectedValue >
                                                                        -1
                                                                    )
                                                                        newSelectedValues.splice(
                                                                            foundSelectedValue,
                                                                            1
                                                                        )
                                                                    else
                                                                        newSelectedValues.push(
                                                                            value[
                                                                                identifier
                                                                            ]
                                                                        )

                                                                    handleChangeSelectedValues(
                                                                        newSelectedValues
                                                                    )
                                                                }}
                                                            />
                                                        )}
                                                        <Box
                                                            fontSize={'13px'}
                                                            sx={{
                                                                flex: 1,
                                                                display: 'flex',
                                                                justifyContent:
                                                                    'start',
                                                                minWidth: 0,
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    maxWidth:
                                                                        '100%',
                                                                    overflow:
                                                                        'hidden',
                                                                    textOverflow:
                                                                        'ellipsis',
                                                                }}
                                                            >
                                                                {renderColumn(
                                                                    value,
                                                                    column
                                                                )}
                                                            </Box>
                                                        </Box>
                                                        {handleClickDetails &&
                                                            index ===
                                                                arr.length -
                                                                    1 && (
                                                                <IconButton
                                                                    size="small"
                                                                    sx={{
                                                                        color: 'primary.main',
                                                                        p: '2px',
                                                                    }}
                                                                    onClick={() =>
                                                                        handleClickDetails(
                                                                            value[
                                                                                identifier
                                                                            ]
                                                                        )
                                                                    }
                                                                >
                                                                    <SubdirectoryArrowRightIcon fontSize="small" />
                                                                </IconButton>
                                                            )}
                                                    </Stack>
                                                </CustomTableCell>
                                            )
                                        )}
                                    </TableRow>
                                )
                            }
                        )}
                    </TableBody>
                </Table>
                {pagination && (
                    <Stack
                        direction="row"
                        width="100%"
                        alignItems={'center'}
                        sx={{
                            borderTop: '1px solid var(--border-color)',
                            px: '16px',
                            py: '10px',
                            backgroundColor: 'var(--surface-muted)',
                        }}
                    >
                        {selectedValues.length > 0 && (
                            <Stack
                                direction={'row'}
                                alignItems={'center'}
                                spacing={1}
                            >
                                <Typography
                                    fontSize={'13px'}
                                    color="text.secondary"
                                >
                                    {selectedValues.length} rows selected
                                </Typography>
                                <Button
                                    size="small"
                                    variant="text"
                                    color="primary"
                                    startIcon={
                                        <VisibilityIcon fontSize="small" />
                                    }
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '12px',
                                    }}
                                    onClick={handleOpenSelectedItems}
                                >
                                    {t('Ver seleccionados')}
                                </Button>
                            </Stack>
                        )}
                        <Stack
                            direction={'row'}
                            alignItems={'center'}
                            justifyContent={'end'}
                            flex={1}
                            spacing={1}
                        >
                            {pagination.rowsCount && (
                                <Typography
                                    fontSize={'13px'}
                                    color="text.secondary"
                                >
                                    {(pagination.page - 1) *
                                        GLOBAL_REQUEST_CONFIGS.rows +
                                        1}{' '}
                                    -{' '}
                                    {pagination.page *
                                        GLOBAL_REQUEST_CONFIGS.rows <
                                    pagination.rowsCount
                                        ? pagination.page *
                                          GLOBAL_REQUEST_CONFIGS.rows
                                        : pagination.rowsCount}{' '}
                                    of {pagination.rowsCount}
                                </Typography>
                            )}
                            <Stack direction={'row'} alignItems={'center'}>
                                <IconButton
                                    size="small"
                                    disabled={pagination.page === 1}
                                    onClick={() =>
                                        pagination.handleChangePagination(
                                            pagination.page - 1,
                                            GLOBAL_REQUEST_CONFIGS.rows
                                        )
                                    }
                                >
                                    <KeyboardArrowLeftIcon />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    disabled={
                                        pagination.rowsCount
                                            ? (pagination.page - 1) *
                                                  GLOBAL_REQUEST_CONFIGS.rows +
                                                  values.length >=
                                              pagination.rowsCount
                                            : values.length !==
                                              GLOBAL_REQUEST_CONFIGS.rows
                                    }
                                    onClick={() => {
                                        pagination.handleChangePagination(
                                            pagination.page + 1,
                                            GLOBAL_REQUEST_CONFIGS.rows
                                        )
                                        /* window.scroll(0, 0) */
                                    }}
                                >
                                    <KeyboardArrowRightIcon />
                                </IconButton>
                            </Stack>
                        </Stack>
                    </Stack>
                )}
            </TableContainer>
        </>
    )
}
